/**
 * Generic redux-form field renderers.
 *
 * These live here rather than under views/Offers so shared components can use them without a
 * components -> views import. Every export takes redux-form's `{ input, meta }` contract; the
 * form wiring is unchanged, only the markup is MUI.
 */
import React from 'react';
import { Link } from 'react-router-dom';
import FormLabel from '@mui/material/FormLabel';
import TextField from '@mui/material/TextField';
import FiberManualRecord from '@mui/icons-material/FiberManualRecord';
import OpenInNew from '@mui/icons-material/OpenInNew';
import { Button, FieldError, Toggle } from '@/ui';
import NativeSelectField from './NativeSelectField';
import ComboBoxAutocomplete from './ComboBoxAutocomplete';
import { toOptionIdentity, toOptionLabel } from './autocompleteHelpers';
import DropdownAutocomplete from './DropdownAutocomplete';
import Autocomplete from '@mui/material/Autocomplete';
import MultiSelectAutocomplete from './MultiSelectAutocomplete';
import NumberTextField from './NumberTextField';

// The label-above metrics every native-select renderer here shares with the FormLabels the forms write
// themselves (and with FormFields/LabelSpacer), so controls in one row start their inputs on one line.
const LABEL_SX = { display: 'block', mb: 0.5 };

export const getIsActiveTrafficLight = (status) =>
  status ? (
    <FiberManualRecord sx={{ fontSize: '0.875rem', color: 'success.main' }} />
  ) : (
    <FiberManualRecord sx={{ fontSize: '0.875rem', color: 'error.main' }} />
  );

// A caller's `helperText` is the field's resting hint; a validation error takes its place while it
// stands, rather than the hint spreading in after it and hiding the message.
export const renderField = ({
  input,
  maxLength,
  disabled,
  label,
  type,
  className,
  placeholder,
  readOnly,
  helperText,
  meta: { touched, error },
  ...rest
}) => {
  return (
    <TextField
      {...input}
      label={label}
      disabled={disabled}
      placeholder={placeholder}
      type={type}
      className={className}
      error={Boolean(touched && error)}
      helperText={touched && error ? error : helperText}
      slotProps={{ input: { readOnly }, htmlInput: { maxLength } }}
      {...rest}
    />
  );
};

/**
 * Multiline sibling of `renderField`, and it handles props the same way, so callers can
 * pass `label`, `placeholder`, `id`, `fullWidth`, `disabled`, `className`, `readOnly` and
 * `maxLength` and have them reach the field.
 *
 * Two props callers already pass are deliberately swallowed instead of forwarded:
 * - `type`: a textarea has no type, so `type='text'` is a habit, not a request.
 * - `required`: forwarding it switches on native browser validation, which would block
 *   submitting forms that deliberately allow the field to be empty — Landing Page script
 *   blocks, and a POST offer action saved without a URL (see `views/Offers/validate`).
 *   Require a value in the form's own `validate` function instead.
 */
export const textarea = ({
  rows,
  input,
  label,
  placeholder = 'Add text here...',
  disabled,
  className,
  readOnly,
  maxLength,
  type: _type,
  required: _required,
  meta: { touched, error },
  ...rest
}) => {
  return (
    <TextField
      {...input}
      multiline
      minRows={Number(rows) || 3}
      label={label}
      placeholder={placeholder}
      disabled={disabled}
      className={className}
      error={Boolean(touched && error)}
      helperText={touched && error ? error : undefined}
      slotProps={{ input: { readOnly }, htmlInput: { maxLength } }}
      {...rest}
    />
  );
};

export const required = (value) => (value ? undefined : 'Required');

export const renderTrueOrFalse = ({ input, type }) => (
  <div>
    <NativeSelectField {...input} type={type}>
      <option value='not_set'>Not set</option>
      <option value='true'>True</option>
      <option value='false'>False</option>
    </NativeSelectField>
  </div>
);

export const renderActionCriteriaAndOr = ({ className, name, input, type, meta: { touched, error } }) => {
  return (
    <div className={className}>
      <NativeSelectField {...input} type={type}>
        <option value='AND'>AND - Suppress this offer if ALL checked actions' criteria fail</option>
        <option value='OR'>OR - Suppress this offer if ANY checked actions' criteria fail</option>
      </NativeSelectField>
      <br />
      <FieldError error={error} touched={touched} />
    </div>
  );
};

export const renderFormTypeDropdown = ({ input, label, type }) => (
  <div>
    <FormLabel sx={LABEL_SX}>{label}</FormLabel>
    <NativeSelectField {...input} type={type}>
      <option value=''>Select Form Type</option>
      <option value='Question'>Question</option>
      <option value='Registration'>Registration</option>
    </NativeSelectField>
  </div>
);

export const renderToggleSwitch = (field) => {
  return (
    <Toggle
      // Forwarded so the `<label htmlFor>` its callers already write reaches the input.
      // The switch never carried an id, so every one of those labels named nothing.
      id={field.id}
      checked={Boolean(field.input.value)}
      onChange={(checked) => field.input.onChange(checked)}
      disabled={field.disabled}
      offColor='error'
    />
  );
};

export const renderActiveToggleSwitch = ({ input }) => {
  return (
    <Toggle
      checked={Boolean(input.value)}
      onChange={(checked) => input.onChange(checked)}
      offColor='error'
      label={input.value ? 'Active' : 'Inactive'}
    />
  );
};

/**
 * A picker you can also add to: react-select's Creatable, now MUI's `freeSolo` Autocomplete.
 *
 * The contract the two call sites read back is unchanged — the field holds `{ id, name }`, and a
 * brand-new entry is announced through `onCreate` rather than written to the field, because both
 * callers dispatch their own `change` once the entity exists. Typing alone still writes nothing,
 * which is why the input is not wired back to `onChange` the way the combobox renderers are.
 */
export const renderCreatableSelect = ({
  input,
  id,
  data,
  disabled,
  onCreate,
  valueField,
  textField,
  meta: { touched, error },
  busy,
  allData,
  link,
}) => {
  const options = allData && allData.length > 0 ? allData : data || [];
  const { value } = input;
  const selected = value ? { [valueField]: value.id ?? value.value, [textField]: value.name ?? value.label } : null;

  return (
    <>
      <div style={{ display: 'flex', alignItems: 'center', width: '100%' }}>
        {link && selected && (
          // One anchor styled as a button, rather than a button nested inside an anchor.
          <Button
            component={Link}
            to={`${link}/${selected[valueField]}`}
            target='_blank'
            color='success'
            title='Visit element'
            aria-label='Visit element'
          >
            <OpenInNew fontSize='small' />
          </Button>
        )}
        <Autocomplete
          freeSolo
          forcePopupIcon
          autoHighlight
          id={id}
          options={options}
          value={selected}
          disabled={disabled}
          loading={busy}
          sx={{ flex: 1 }}
          getOptionLabel={toOptionLabel(textField)}
          {...toOptionIdentity(valueField)}
          onChange={(_event, picked) => {
            if (typeof picked === 'string') {
              // A value typed rather than picked — react-select called this onCreateOption.
              onCreate(picked);
              return;
            }
            input.onChange(picked ? { id: picked[valueField], name: picked[textField] } : null);
          }}
          // Called with no argument on purpose: redux-form would take the event as the field's value.
          onBlur={() => input.onBlur()}
          renderInput={(params) => (
            <TextField {...params} name={input.name} error={Boolean(touched && error)} />
          )}
        />
      </div>
      <br />
      <FieldError error={error} touched={touched} />
    </>
  );
};

export const renderMultiSelect = ({
  input,
  id,
  data,
  disabled,
  valueField,
  textField,
  placeholder,
  allowCreate = false,
  dataKey,
  busy,
  groupBy,
  renderListGroup,
  meta: { touched, error },
}) => (
  <>
    <MultiSelectAutocomplete
      id={id}
      name={input.name}
      options={data}
      value={input.value}
      onChange={input.onChange}
      onBlur={() => input.onBlur()}
      textField={textField}
      // `valueField` had been inert: the widget only ever read `dataKey`, so the two callers
      // passing it got identity comparison instead. Honouring it as a fallback is what they meant,
      // and it stops MUI warning that values loaded with the record match none of the separately
      // fetched options — which is exactly the case those two are in.
      dataKey={dataKey || valueField}
      placeholder={placeholder}
      disabled={disabled}
      busy={busy}
      allowCreate={allowCreate}
      groupBy={groupBy}
      renderListGroup={renderListGroup}
      error={Boolean(touched && error)}
    />
    <br />
    <FieldError error={error} touched={touched} />
  </>
);

export const RenderCreatableMultiSelect = ({ input, meta, data, valueField, textField, placeholder, onCreate }) => {
  // react-widgets reported a created entry as the typed string through `onCreate`; MUI's freeSolo
  // appends that string to the value array instead, so it is shaped here into the object the rest
  // of the form expects. A string can only be in `selected` on the render it was typed, so
  // `onCreate` still fires once per creation.
  const handleChange = (selected) =>
    input.onChange(
      selected.map((item) => {
        if (typeof item !== 'string') return item;
        if (onCreate) onCreate(item);
        return { [valueField]: item, [textField]: item };
      })
    );

  return (
    <>
      <MultiSelectAutocomplete
        name={input.name}
        options={data || []}
        value={input.value}
        onChange={handleChange}
        onBlur={input.onBlur}
        allowCreate
        textField={textField}
        dataKey={valueField}
        placeholder={placeholder}
        error={Boolean(meta.touched && meta.error)}
      />
      <FieldError error={meta.error} touched={meta.touched} />
    </>
  );
};

export const renderOfferActionDropdown = ({ input, label, type, disabled, meta: { touched, error } }) => (
  <div>
    <div>
      <FormLabel sx={LABEL_SX}>{label}</FormLabel>
      <NativeSelectField {...input} disabled={disabled} type={type}>
        <option value='POST'>Post</option>
        <option value='LINKOUT'>Linkout</option>
        <option value='USER_ATTRIBUTE'>Set Attribute</option>
        <option value='POST_WATERFALL'>Post Waterfall</option>
        <option value='LINKOUT_WATERFALL'>Linkout Waterfall</option>
        <option value='KAFKA_PUBLISH_EVENT'>Publish (Kafka)</option>
        <option value='DESTINATION_WATERFALL'>Destination Waterfall</option>
      </NativeSelectField>
      {touched && error && <span>{error}</span>}
    </div>
  </div>
);

export const renderOfferPostAction = ({
  input,
  label,
  defaultValue,
  disabled,
  type,
  meta: { touched, error },
  entityName = null,
}) => {
  return (
    <div>
      <FormLabel sx={LABEL_SX}>{label}</FormLabel>
      <NativeSelectField {...input} disabled={disabled} value={defaultValue || input.value} type={type}>
        <option value='ON_IMPRESSION'>Impression</option>
        <option value='ON_CLICK'>Clicked</option>
        <option value='ON_CLICK_OR_SKIP'>Clicked or skipped</option>
        {!entityName || !['iframe', 'offer wall ad'].includes(entityName.toLowerCase()) ? (
          <option value='ON_QUESTION_CLICK'>TinyMCE/Question clicked</option>
        ) : null}
      </NativeSelectField>
      {touched && error && <span>{error}</span>}
    </div>
  );
};

export const renderLandingPageTriggerTypes = ({
  input,
  label,
  defaultValue,
  disabled,
  type,
  meta: { touched, error },
}) => (
  <div>
    <div>
      <FormLabel sx={LABEL_SX}>{label}</FormLabel>
      <NativeSelectField {...input} disabled={disabled} value={defaultValue ? defaultValue : input.value} type={type}>
        <option value='ON_IMPRESSION'>Impression</option>
        <option value='SUBMIT'>Submit</option>
        <option value='SUBMIT+PARAMS'>Submit + Params</option>
        <option value='SUBMIT+CERTIFIED_LOOKUP'>Submit + Certified Lookup</option>
      </NativeSelectField>
      {touched && error && <span>{error}</span>}
    </div>
  </div>
);

export const renderOfferPostMethod = ({ input, label, isOfferAction, type, meta: { touched, error } }) => {
  return (
    <div>
      <div>
        <FormLabel sx={LABEL_SX}>{label}</FormLabel>
        <NativeSelectField {...input} type={type}>
          {!isOfferAction && <option value='NONE'>NONE</option>}
          <option value='GET'>GET</option>
          <option value='POST'>POST</option>
          <option value='JSON'>JSON</option>
          <option value='XML'>XML</option>
        </NativeSelectField>
        {touched && error && <span>{error}</span>}
      </div>
    </div>
  );
};

export const renderPublishMethod = ({ input, label, type, meta: { touched, error } }) => {
  return (
    <div>
      <div>
        <FormLabel sx={LABEL_SX}>{label}</FormLabel>
        <NativeSelectField {...input} type={type}>
          <option value='JSON'>JSON</option>
          <option value='AVRO' disabled>
            AVRO (coming soon)
          </option>
        </NativeSelectField>
        {touched && error && <span>{error}</span>}
      </div>
    </div>
  );
};

export const renderPrePingMethod = ({ input, label, type, meta: { touched, error } }) => (
  <div>
    <FormLabel sx={LABEL_SX}>{label}</FormLabel>
    <NativeSelectField {...input} type={type} value={input.value}>
      <option value='NONE'>NONE</option>
      <option value='GET'>GET</option>
      <option value='POST'>POST</option>
      <option value='JSON'>JSON</option>
      <option value='XML'>XML</option>
    </NativeSelectField>
    <br />
    <FieldError error={error} touched={touched} />
  </div>
);

export const renderOnRevenue = ({ input, label, type, meta: { touched, error } }) => (
  <div>
    <div>
      <FormLabel sx={LABEL_SX}>{label}</FormLabel>
      <NativeSelectField {...input} type={type}>
        <option value={null}>None</option>
        <option value='Success'>Success</option>
        <option value='Gross'>Gross</option>
      </NativeSelectField>
      {touched && error && <span>{error}</span>}
    </div>
  </div>
);

export const renderTriggerDropdown = ({ input, label, type, meta: { touched, error }, formType }) => (
  <div>
    <div>
      <FormLabel sx={LABEL_SX}>{label}</FormLabel>
      <NativeSelectField {...input} type={type}>
        {/* <option value='null'>Select method...</option> */}
        <option value='SUBMIT'>Submit</option>
        <option value='SUBMIT+PARAMS'>Submit+Params</option>
        <option value='SUBMIT+CERTIFIED_LOOKUP'>Submit+Certified Lookup</option>
        <option value='ON_IMPRESSION'>Impression</option>
        {formType && formType.toLowerCase() == 'question' && (
          <option value='ON_QUESTION_CLICK'>TinyMCE/Question clicked</option>
        )}
      </NativeSelectField>
      {touched && error && <span>{error}</span>}
    </div>
  </div>
);

export const renderDailyRestrictionDay = ({ input, label, type }) => (
  <div>
    <div>
      <div>
        <FormLabel sx={{ display: 'block', mb: 0.5 }}>{label}</FormLabel>
        <NativeSelectField {...input} type={type}>
          <option value=''>Select Day</option>
          <option value='sunday'>Sunday</option>
          <option value='monday'>Monday</option>
          <option value='tuesday'>Tuesday</option>
          <option value='wednesday'>Wednesday</option>
          <option value='thursday'>Thursday</option>
          <option value='friday'>Friday</option>
          <option value='saturday'>Saturday</option>
          <option value='weekdays'>Weekdays</option>
          <option value='weekends'>Weekends</option>
        </NativeSelectField>
      </div>
    </div>
  </div>
);

export const renderRestrictions = ({ input, label, type, meta: { touched, error } }) => {
  return (
    <div>
      <div>
        <FormLabel sx={{ display: 'block', mb: 0.5 }}>{label}</FormLabel>
        <NativeSelectField {...input} type={type}>
          <option value=''>Select Restriction</option>
          <option value='Available'>Available</option>
          <option value='Unavailable'>Unavailable</option>
          <option value='Partially Restricted'>Partially Restricted</option>
        </NativeSelectField>
        <br />
        <FieldError error={error} touched={touched} />
      </div>
    </div>
  );
};

export const renderDropdownList = ({
  input,
  data,
  disabled,
  valueField,
  defaultValue,
  textField,
  placeholder,
  busy = false,
  meta: { touched, error },
  useValueFieldAsInputValue = false,
}) => {
  const { value, name } = input;
  const vf = valueField || 'value';

  return (
    <>
      <DropdownAutocomplete
        id={name}
        name={name}
        options={data}
        // `value ?? defaultValue` exactly as before: a field explicitly holding '' stays empty
        // rather than falling back, so what is displayed keeps matching what would be submitted.
        value={value == null ? defaultValue : value}
        valueField={valueField}
        textField={textField}
        placeholder={placeholder}
        disabled={disabled}
        busy={busy}
        error={Boolean(touched && error)}
        onChange={(selected) => {
          if (useValueFieldAsInputValue) {
            input.onChange(selected && typeof selected === 'object' ? selected[vf] : selected ?? null);
            return;
          }
          input.onChange(selected);
        }}
        // Called with no argument on purpose: redux-form would take the event as the field's value.
        onBlur={() => input.onBlur()}
      />
      <br />
      <FieldError error={error} touched={touched} />
    </>
  );
};

export const renderComboBoxSecond = ({ input, data, valueField, textField, onSelect, busy = false }) => (
  <ComboBoxAutocomplete
    name={input.name}
    options={data}
    value={input.value}
    textField={textField}
    valueField={valueField}
    busy={busy}
    // This one stores the value field rather than the option, which is the contract its one
    // caller reads back.
    onChange={(selected) => input.onChange(selected && typeof selected === 'object' ? selected[valueField] : selected)}
    onSelect={onSelect}
    // Called with no argument on purpose: redux-form would take the event as the field's value.
    onBlur={() => input.onBlur()}
  />
);

export const renderComboBox = ({
  input,
  id,
  data,
  disabled,
  onChange,
  onSelect,
  textField,
  valueField,
  placeholder,
  meta: { touched, error },
  busy,
  allData,
  link,
}) => (
  <>
    {/* The launch button follows the field it belongs to, with a gap: leading it, flush against
        the input, it read as part of the control rather than an action on what the control holds. */}
    <div style={{ display: 'flex', alignItems: 'center', width: '100%', gap: 8 }}>
      <ComboBoxAutocomplete
        id={id}
        name={input.name}
        options={allData && allData.length > 0 ? allData : data}
        value={input.value}
        disabled={disabled}
        textField={textField}
        valueField={valueField}
        placeholder={placeholder}
        busy={busy}
        error={Boolean(touched && error)}
        sx={{ flex: 1 }}
        onChange={onChange || input.onChange}
        onSelect={onSelect}
        // Called with no argument on purpose: redux-form would take the event as the field's value.
        onBlur={() => input.onBlur()}
      />
      {link && (
        // One anchor styled as a button, rather than a button nested inside an anchor.
        <Button
          component={Link}
          to={`${link}/${input.value.id}`}
          target='_blank'
          color='success'
          title='Visit element'
          aria-label='Visit element'
        >
          <OpenInNew fontSize='small' />
        </Button>
      )}
    </div>
    <br />
    <FieldError error={error} touched={touched} />
  </>
);

export const renderCriteriaOptions = ({ input, type, meta: { touched, error } }) => (
  <div>
    <NativeSelectField {...input} type={type}>
      <option value='None'>None</option>
      <option value='Include'>Include</option>
      <option value='Exclude'>Exclude</option>
    </NativeSelectField>
    <br />
    <FieldError error={error} touched={touched} />
  </div>
);

export const renderDailyHourRestrictions = ({ input, label, type, meta: { error, touched } }) => {
  return (
    <div>
      <FormLabel sx={LABEL_SX}>{label}</FormLabel>
      <NativeSelectField {...input} type={type}>
        <option value=''>Select a time restriction...</option>
        <option value='12:00 AM'>12:00 AM</option>
        <option value='12:15 AM'>12:15 AM</option>
        <option value='12:30 AM'>12:30 AM</option>
        <option value='12:45 AM'>12:45 AM</option>
        <option value='1:00 AM'>1:00 AM</option>
        <option value='1:15 AM'>1:15 AM</option>
        <option value='1:30 AM'>1:30 AM</option>
        <option value='1:45 AM'>1:45 AM</option>
        <option value='2:00 AM'>2:00 AM</option>
        <option value='2:15 AM'>2:15 AM</option>
        <option value='2:30 AM'>2:30 AM</option>
        <option value='2:45 AM'>2:45 AM</option>
        <option value='3:00 AM'>3:00 AM</option>
        <option value='3:15 AM'>3:15 AM</option>
        <option value='3:30 AM'>3:30 AM</option>
        <option value='3:45 AM'>3:45 AM</option>
        <option value='4:00 AM'>4:00 AM</option>
        <option value='4:15 AM'>4:15 AM</option>
        <option value='4:30 AM'>4:30 AM</option>
        <option value='4:45 AM'>4:45 AM</option>
        <option value='5:00 AM'>5:00 AM</option>
        <option value='5:15 AM'>5:15 AM</option>
        <option value='5:30 AM'>5:30 AM</option>
        <option value='5:45 AM'>5:45 AM</option>
        <option value='6:00 AM'>6:00 AM</option>
        <option value='6:15 AM'>6:15 AM</option>
        <option value='6:30 AM'>6:30 AM</option>
        <option value='6:45 AM'>6:45 AM</option>
        <option value='7:00 AM'>7:00 AM</option>
        <option value='7:15 AM'>7:15 AM</option>
        <option value='7:30 AM'>7:30 AM</option>
        <option value='7:45 AM'>7:45 AM</option>
        <option value='8:00 AM'>8:00 AM</option>
        <option value='8:15 AM'>8:15 AM</option>
        <option value='8:30 AM'>8:30 AM</option>
        <option value='8:45 AM'>8:45 AM</option>
        <option value='9:00 AM'>9:00 AM</option>
        <option value='9:15 AM'>9:15 AM</option>
        <option value='9:30 AM'>9:30 AM</option>
        <option value='9:45 AM'>9:45 AM</option>
        <option value='10:00 AM'>10:00 AM</option>
        <option value='10:15 AM'>10:15 AM</option>
        <option value='10:30 AM'>10:30 AM</option>
        <option value='10:45 AM'>10:45 AM</option>
        <option value='11:00 AM'>11:00 AM</option>
        <option value='11:15 AM'>11:15 AM</option>
        <option value='11:30 AM'>11:30 AM</option>
        <option value='11:45 AM'>11:45 AM</option>
        <option value='12:00 PM'>12:00 PM</option>
        <option value='12:15 PM'>12:15 PM</option>
        <option value='12:30 PM'>12:30 PM</option>
        <option value='12:45 PM'>12:45 PM</option>
        <option value='1:00 PM'>1:00 PM</option>
        <option value='1:15 PM'>1:15 PM</option>
        <option value='1:30 PM'>1:30 PM</option>
        <option value='1:45 PM'>1:45 PM</option>
        <option value='2:00 PM'>2:00 PM</option>
        <option value='2:15 PM'>2:15 PM</option>
        <option value='2:30 PM'>2:30 PM</option>
        <option value='2:45 PM'>2:45 PM</option>
        <option value='3:00 PM'>3:00 PM</option>
        <option value='3:15 PM'>3:15 PM</option>
        <option value='3:30 PM'>3:30 PM</option>
        <option value='3:45 PM'>3:45 PM</option>
        <option value='4:00 PM'>4:00 PM</option>
        <option value='4:15 PM'>4:15 PM</option>
        <option value='4:30 PM'>4:30 PM</option>
        <option value='4:45 PM'>4:45 PM</option>
        <option value='5:00 PM'>5:00 PM</option>
        <option value='5:15 PM'>5:15 PM</option>
        <option value='5:30 PM'>5:30 PM</option>
        <option value='5:45 PM'>5:45 PM</option>
        <option value='6:00 PM'>6:00 PM</option>
        <option value='6:15 PM'>6:15 PM</option>
        <option value='6:30 PM'>6:30 PM</option>
        <option value='6:45 PM'>6:45 PM</option>
        <option value='7:00 PM'>7:00 PM</option>
        <option value='7:15 PM'>7:15 PM</option>
        <option value='7:30 PM'>7:30 PM</option>
        <option value='7:45 PM'>7:45 PM</option>
        <option value='8:00 PM'>8:00 PM</option>
        <option value='8:15 PM'>8:15 PM</option>
        <option value='8:30 PM'>8:30 PM</option>
        <option value='8:45 PM'>8:45 PM</option>
        <option value='9:00 PM'>9:00 PM</option>
        <option value='9:15 PM'>9:15 PM</option>
        <option value='9:30 PM'>9:30 PM</option>
        <option value='9:45 PM'>9:45 PM</option>
        <option value='10:00 PM'>10:00 PM</option>
        <option value='10:15 PM'>10:15 PM</option>
        <option value='10:30 PM'>10:30 PM</option>
        <option value='10:45 PM'>10:45 PM</option>
        <option value='11:00 PM'>11:00 PM</option>
        <option value='11:15 PM'>11:15 PM</option>
        <option value='11:30 PM'>11:30 PM</option>
        <option value='11:45 PM'>11:45 PM</option>
      </NativeSelectField>
      <br />
      <FieldError error={error} touched={touched} />
    </div>
  );
};

export const renderQuestionType = ({ input, label, type }) => (
  <div>
    <FormLabel sx={LABEL_SX}>{label}</FormLabel>
    <NativeSelectField {...input} type={type}>
      <option value=''>Select</option>
      <option value='Yes/No'>Yes/No</option>
      <option value='Multi-option'>Multi-option</option>
      <option value='Multi-select'>Multi-select</option>
    </NativeSelectField>
  </div>
);

export const RenderNumberPicker = ({ input, meta, placeholder, disabled, precision, step, defaultValue }) => (
  <div>
    <NumberTextField
      name={input.name}
      value={input.value === '' || input.value === null || input.value === undefined ? defaultValue : input.value}
      onChange={input.onChange}
      placeholder={placeholder}
      disabled={disabled}
      min={0}
      step={step ?? (precision ? 10 ** -precision : 1)}
      error={Boolean(meta.touched && meta.error)}
    />
    <br />
    <FieldError error={meta.error} touched={meta.touched} />
  </div>
);

export const CapIntervals = ({ input, type, meta: { touched, error } }) => (
  <div>
    <NativeSelectField {...input} type={type}>
      <option value=''>None</option>
      <option value='Daily'>Daily</option>
      <option value='Monthly'>Monthly</option>
      <option value='Lifetime'>Lifetime</option>
    </NativeSelectField>
    <br />
    <FieldError error={error} touched={touched} />
  </div>
);
